import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Close } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';
import type { SignatureV2ParticipantInput } from '@/types/signatureV2';

import { ModalActions, ModalForm, ModalTitle } from './styles';

interface ModalProps {
  showModal: boolean;
  initialSigner?: SignatureV2ParticipantInput | null;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSave: (signer: SignatureV2ParticipantInput) => void;
}

interface FormData {
  name: string;
  email: string;
}

const AddMoreSignersModal = ({ showModal, initialSigner, setShowModal, onSave }: ModalProps) => {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t: typedT } = useTranslation();
  const t = typedT as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { emailRegister, inputRegister } = useAuthValidation();
  const {
    reset,
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<FormData>({ defaultValues: { name: '', email: '' } });

  useEffect(() => {
    reset({ name: initialSigner?.name || '', email: initialSigner?.email || '' });
  }, [initialSigner, reset, showModal]);

  const onSubmit = handleSubmit((data) => {
    onSave({ name: data.name.trim(), email: data.email.trim() });
    setShowModal(false);
  });

  return (
    <Modal open={showModal} onClose={() => setShowModal(false)} disableEnforceFocus>
      <ModalForm onSubmit={onSubmit} aria-labelledby="add-more-signers-title">
        <Close.ButtonClose type="button" onClick={() => setShowModal(false)}>
          <Close.CloseSVG />
        </Close.ButtonClose>
        <ModalTitle id="add-more-signers-title">{t('signatureV2.contract.addMoreSigners')}</ModalTitle>
        <InputWrapper>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('signatureV2.contract.nameRequired')}
                variant="standard"
                sx={{ width: '100%', marginTop: '15px' }}
                error={!!error}
                helperText={error?.message || null}
                autoComplete="off"
              />
            )}
            rules={inputRegister}
          />
        </InputWrapper>

        <InputWrapper>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('signatureV2.contract.emailRequired')}
                variant="standard"
                sx={{ width: '100%', marginTop: '15px' }}
                error={!!error}
                helperText={error?.message || null}
                autoComplete="off"
              />
            )}
            rules={emailRegister}
          />
        </InputWrapper>
        <ModalActions>
          <Button type="submit" size="lg" variant="primary" rounded={false} disabled={!isDirty}>
            {t('buttons.ok')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            color="primary"
            onClick={() => setShowModal(false)}
            style={{ textDecoration: 'underline' }}
          >
            {t('buttons.cancel')}
          </Button>
        </ModalActions>
      </ModalForm>
    </Modal>
  );
};

export default AddMoreSignersModal;
